import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { ensureTextOnBg } from '@utils/helpers/color-utils';
import { useGetMe } from '@utils/api/users/use-get-me';
import { useOrganization } from '@utils/api/users/use-get-organization';
import { useEntitlements } from '@utils/providers/entitlements-context';
import { WorkspaceTable } from '@components/main-workspace/workspace-table';
import { CreateFolder } from '@components/workspace/create-folder';
import UploadButton from '@components/shared/upload/upload-button';
import Button from '@components/atoms/ui-kit/button';
import { SortOrderToggle } from '@components/workspace/sort-order-toggle';
import {
  getSortOrder,
  setSortOrder,
  type SortOrder
} from '@utils/localStorage/sort-order-storage';
import { FolderPlus } from 'lucide-react';
import { Heading } from '@components/atoms/ui-kit/text';
import { useTopBarSlots } from '@components/layout/top-bar-context';

function getGreetingKey(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'good-morning';
  if (hour < 18) return 'good-afternoon';
  return 'good-evening';
}

export default function Home() {
  const { t } = useTranslation();
  const { getColour, getBrandTint } = useThemeContext();
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);
  const [sortOrder, setSortOrderState] = useState<SortOrder>(getSortOrder);
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: meData } = useGetMe();
  const { data: org } = useOrganization();
  const { entitlements } = useEntitlements();

  // True when folders-first was applied by the global search "see all folders"
  // shortcut (not by the user's own toggle). Resets when global search is typed.
  const fromGlobalSearchRef = useRef(false);

  const handleSortOrderChange = (order: SortOrder) => {
    fromGlobalSearchRef.current = false;
    setSortOrderState(order);
    setSortOrder(order);
  };

  // When global search "see all folders" lands here with ?sortOrder=folders-first:
  // - apply it to state only (don't write to localStorage — it's a temporary view)
  // - scrub the param so a manual refresh doesn't re-apply it
  const sortOrderParam = searchParams.get('sortOrder') as SortOrder | null;
  useEffect(() => {
    if (
      sortOrderParam === 'folders-first' ||
      sortOrderParam === 'files-first' ||
      sortOrderParam === 'default'
    ) {
      fromGlobalSearchRef.current = true;
      setSortOrderState(sortOrderParam);
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.delete('sortOrder');
          return next;
        },
        { replace: true }
      );
    }
  }, [sortOrderParam]);

  // When the user edits the global search input, deactivate the temporary sort
  // so it doesn't linger while they search for something unrelated.
  useEffect(() => {
    const reset = () => {
      if (fromGlobalSearchRef.current) {
        fromGlobalSearchRef.current = false;
        setSortOrderState(getSortOrder());
      }
    };
    window.addEventListener('globalSearchTyping', reset);
    return () => window.removeEventListener('globalSearchTyping', reset);
  }, []);

  const firstName = meData?.result?.first_name ?? '';
  const orgName = org?.name ?? '';
  const isTrial = entitlements?.is_trial ?? false;

  // Populate the persistent desktop top bar (#2021). Mobile shows its own
  // in-body header below; the slots have no effect there because TopBar
  // isn't mounted at that breakpoint.
  useTopBarSlots({
    left: (
      <div className="flex items-center gap-2 min-w-0">
        {/* Greeting chip — bg-app-soft + hairline so it reads as a soft
            surface against the bg-app top bar, but is muted enough to feel
            informational rather than primary chrome. */}
        <span
          className="inline-flex items-center gap-2 max-w-full bg-app-soft border border-hairline rounded-full px-5 py-1.5 text-base font-bold tracking-tight truncate"
          style={{ color: getColour('normalText') }}
        >
          <span className="truncate">
            {t(getGreetingKey())}, {firstName || orgName}
          </span>
          {isTrial && (
            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold shrink-0 uppercase tracking-wide"
              style={{
                backgroundColor: getBrandTint(0.15),
                color: ensureTextOnBg(
                  getColour('activeText'),
                  getColour('appBackground'),
                  5.5
                )
              }}
            >
              {t('trial')}
            </span>
          )}
        </span>
      </div>
    ),
    right: (
      <div className="flex items-center gap-1 shrink-0">
        <SortOrderToggle
          sortOrder={sortOrder}
          onSortOrderChange={handleSortOrderChange}
        />
        <UploadButton />
        <Button
          label={t('folder')}
          onClick={() => setIsCreateFolderOpen(true)}
          variant="secondary"
          size="small"
          icon={<FolderPlus size={14} />}
          title={t('create-folder')}
        />
      </div>
    )
  });

  return (
    <div className="bg-app flex-1 min-h-0 flex flex-col">
      <div className="w-full flex-1 min-h-0 flex flex-col gap-y-2 pb-2 pt-1">
        {/* Small/medium: simplified header — desktop uses the persistent TopBar
            slots above. Hidden at the same breakpoint TopBar appears (LARGE_TABLET = 1024px). */}
        <div
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-6 md:px-4 py-2.5 gap-2 md:hidden"
          style={{ borderBottom: `1px solid ${getColour('hairline')}` }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <Heading level={1} size="lg" truncate>
              {t(getGreetingKey())}, {firstName || orgName}
            </Heading>
            {isTrial && (
              <span
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold shrink-0"
                style={{
                  backgroundColor: getBrandTint(0.12),
                  color: getColour('activeText')
                }}
              >
                {t('trial')}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <SortOrderToggle
              sortOrder={sortOrder}
              onSortOrderChange={handleSortOrderChange}
            />
            <UploadButton />
            <Button
              label={t('folder')}
              onClick={() => setIsCreateFolderOpen(true)}
              variant="secondary"
              size="small"
              icon={<FolderPlus size={14} />}
              title={t('create-folder')}
            />
          </div>
        </div>

        {/* Workspace */}
        <div className="px-2 flex-1 min-h-0 flex flex-col">
          <WorkspaceTable sortOrder={sortOrder} />
        </div>
      </div>

      <CreateFolder
        isOpen={isCreateFolderOpen}
        onClose={() => setIsCreateFolderOpen(false)}
      />
    </div>
  );
}
